// ─────────────────────────────────────────────────────────────────────────────
// Cómo se escribe el nombre de una persona en todo el sistema.
//
// POR QUÉ EXISTE ESTE ARCHIVO
// Los datos vienen escritos de dos formas distintas según de dónde salieron: lo histórico está
// en MAYÚSCULAS sin acentos ("GOMEZ, JOSE ALBERTO") y todo lo que carga el chatbot viene bien
// escrito ("Juárez, Sebastián Ariel"). De 758 turnos, 622 tenían el nombre en formato mixto.
// Además el orden estaba a criterio de cada pantalla: unas mostraban "Nombre Apellido" y otras
// "Apellido Nombre", así que la misma persona se veía distinta según dónde se la mirara.
//
// La decisión del estudio es MAYÚSCULAS y siempre "APELLIDO, NOMBRE". Pasar a mayúsculas no
// pierde nada: toUpperCase() conserva los acentos y la ñ ("Juárez" → "JUÁREZ"), lo único que se
// pierde es la distinción mayúscula/minúscula, que es justamente lo que sobra acá.
//
// Esto resuelve la presentación. Los datos guardados se normalizan aparte (backend al guardar +
// migración V218): sin eso, lo que ordena SQL —los listados por apellido— sigue mezclado, y lo
// que no pasa por React (PDF, Excel, WhatsApp, mails) se sigue viendo desparejo.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Deja un texto listo para mostrar: mayúsculas, sin espacios en los bordes y sin espacios
 * dobles adentro (hay nombres guardados como "JOSE  ALBERTO " que si no se ven partidos).
 */
export function textoPersona(valor) {
    if (valor === null || valor === undefined) return '';
    return String(valor).trim().replace(/\s+/g, ' ').toUpperCase();
}

/**
 * El formato de listado: "PEREZ, JUAN CARLOS".
 *
 * Si falta una de las dos partes devuelve la que haya, sin la coma colgando: un turno viejo sin
 * apellido tiene que verse "JUAN CARLOS", no ", JUAN CARLOS".
 */
export function apellidoNombre(apellido, nombre) {
    const a = textoPersona(apellido);
    const n = textoPersona(nombre);
    if (a && n) return `${a}, ${n}`;
    return a || n;
}

/** Igual que apellidoNombre pero tomando el objeto (cliente, turno, contacto…). */
export function apellidoNombreDe(persona) {
    return apellidoNombre(persona?.apellido, persona?.nombre);
}

/**
 * "JUAN CARLOS PEREZ" — el orden natural del habla.
 *
 * Se usa SOLO dentro de una frase, donde el orden de listado suena raro: el cartel de recepción
 * dice "Llegó JUAN PEREZ", no "Llegó PEREZ, JUAN". Para cualquier listado, tabla, buscador o
 * ficha va apellidoNombre(): ahí lo que importa es poder barrer la columna con la vista.
 */
export function nombreApellido(nombre, apellido) {
    const n = textoPersona(nombre);
    const a = textoPersona(apellido);
    if (n && a) return `${n} ${a}`;
    return n || a;
}

/** Igual que nombreApellido pero tomando el objeto. */
export function nombreApellidoDe(persona) {
    return nombreApellido(persona?.nombre, persona?.apellido);
}

/**
 * Si el turno se sacó a nombre de otra persona que el cliente vinculado (un familiar que llamó o
 * acompañó), devuelve ese nombre ("SUAREZ, OSVALDO MIGUEL") para mostrarlo; si es la misma
 * persona o no hay cliente vinculado, null. Se compara por APELLIDO y sin acentos: el nombre de
 * pila del turno suele venir abreviado o con errores de tipeo, y eso no es "otra persona".
 */
export function turnoANombreDeOtraPersona(turno) {
    if (!turno?.cuilCliente) return null;
    const palabras = (v) => textoPersona(v).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .split(/[\s,]+/).filter(Boolean);
    const apellidoTurno = palabras(turno.apellido);
    const delCliente = new Set(palabras(`${turno.clienteApellido || ''} ${turno.clienteNombre || ''}`));
    if (apellidoTurno.length === 0 || delCliente.size === 0) return null;
    if (apellidoTurno.some((p) => delCliente.has(p))) return null;
    return apellidoNombre(turno.apellido, turno.nombre);
}
